import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { yearService } from '../services/yearService';

interface YearContextType {
    currentYear: Year | null;
    availableYears: Year[];
    loading: boolean;
    error: string | null;
    setCurrentYear: (year: Year) => Promise<void>;
    clearCurrentYear: () => void;
    fetchAvailableYears: (entityId: string, areaId?: string) => Promise<void>;
}

export interface Year {
    ano_fiscal_id: number;
    ano: number;
    descricao: string;
    dtInicio: string;
    dtFim: string;
    status: string;
}

const YearContext = createContext<YearContextType | undefined>(undefined);

export const useYear = () => {
    const context = useContext(YearContext);
    if (!context) {
        throw new Error('useYear must be used within a YearProvider');
    }
    return context;
};

interface YearProviderProps {
    children: ReactNode;
}

export const YearProvider: React.FC<YearProviderProps> = ({ children }) => {
    const [currentYear, setCurrentYearState] = useState<Year | null>(() => {
        // Initialize from localStorage if available
        // const storedYearId = yearService.getCurrentYear();
        return null; // We'll set the actual year after fetching available years
    });
    const [availableYears, setAvailableYears] = useState<Year[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchAvailableYears = useCallback(async (entityId: string, areaId?: string) => {
        try {
            setLoading(true);
            setError(null);

            // If areaId is not provided, try to get it from localStorage
            const currentAreaId = areaId || localStorage.getItem('currentAreaId');

            if (!currentAreaId) {
                setError('Nenhuma área selecionada');
                setLoading(false);
                return;
            }

            const years = await yearService.getAvailableYears(entityId, currentAreaId);
            setAvailableYears(years);

            // If there's a stored year ID, find and set it
            const storedYearId = yearService.getCurrentYear();
            if (storedYearId) {
                const year = years.find(y => y.ano_fiscal_id === storedYearId);
                if (year) {
                    setCurrentYearState(year);
                }
            }
        } catch (err) {
            setError('Erro ao carregar anos disponíveis');
            console.error('Error fetching years:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const setCurrentYear = useCallback(async (year: Year) => {
        try {
            // Optimistically update the UI
            setCurrentYearState(year);
            // Then update storage in the background
            await yearService.setCurrentYear(year.ano_fiscal_id);
        } catch (err) {
            // Revert on error
            setCurrentYearState(currentYear);
            setError('Erro ao definir o ano atual');
            console.error('Error setting current year:', err);
        }
    }, [currentYear]);

    const clearCurrentYear = useCallback(() => {
        localStorage.removeItem('currentYearId');
        setCurrentYearState(null);
    }, []);

    const value = useMemo(() => ({
        currentYear,
        availableYears,
        loading,
        error,
        setCurrentYear,
        clearCurrentYear,
        fetchAvailableYears
    }), [
        currentYear,
        availableYears,
        loading,
        error,
        setCurrentYear,
        clearCurrentYear,
        fetchAvailableYears
    ]);

    return (
        <YearContext.Provider value={value}>
            {children}
        </YearContext.Provider>
    );
}; 